<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-select
          v-model="inputPathname"
          :items="productOptions"
          label="Select Product"
          item-title="name"
          item-value="pathname"
          clearable
          class="mb-4"
        ></v-select>
      </v-col>
    </v-row>

    <v-card v-if="product" class="pa-4">
      <v-card-title class="text-h4 mb-4">{{ product.name }}</v-card-title>
      
      <v-card-text>
        <div class="text-body-1"><strong>Meta Title:</strong> {{ product.metaTitle }}</div>
        <div class="text-body-1"><strong>Meta Description:</strong> {{ product.metaDescription }}</div>
        <div class="text-body-1"><strong>Main Category:</strong> {{ product.mainCategory }}</div>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h5 mb-2">Images</h2>
        <v-row>
          <v-col v-for="(image, index) in product.images" :key="index" cols="12" sm="6" md="4">
            <v-img :src="image.src" :alt="image.alt" height="200" contain></v-img>
            <div class="text-caption mt-2">Type: {{ image.type }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h5 mb-2">Description</h2>
        <div class="text-body-1">{{product.description.descriptionHtml}}</div>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h5 mb-2">Price</h2>
        <div class="text-body-1">{{ product.price }}</div>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h5 mb-2">Specification</h2>
        <v-list>
          <v-list-item><strong>Suitable For:</strong> {{ product.specification.suitableFor }}</v-list-item>
          <v-list-item><strong>Size:</strong> {{ product.specification.size }}</v-list-item>
          <v-list-item><strong>Required Space:</strong> {{ product.specification.requiredSpace }}</v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="openPage">
          Open Product
        </v-btn>
        <v-btn color="secondary" @click="copySource">
          Copy Source
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-alert v-else-if="inputPathname && !product" type="warning" class="mt-4">
      No product found for pathname: "{{ inputPathname }}"
    </v-alert>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useProductsStore } from '@/store/productsStore';
import { useUrlSourcesStore  } from '@/store/urlSourcesStore';
import UrlSourceTypes from '@/enums/UrlSourceTypes';
import { useActiveUrlStore } from '@/store/activeUrlStore';

const productsStore = useProductsStore();
const urlSourcesStore = useUrlSourcesStore ();
const activeUrlStore = useActiveUrlStore();

const url = activeUrlStore.activeUrl;
const inputPathname = ref<string>('');

const productOptions = computed(() => 
urlSourcesStore.getAllPathnamesByType(UrlSourceTypes.PRODUCT)
    .map(path => ({
      pathname: path,
      name: formatProductName(path)
    }))
);

const product = computed(() =>
  inputPathname.value ? productsStore.getProductByPathname(inputPathname.value) : undefined
);

function formatProductName(path: string): string {
  return path
    .replace('/product/', '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}
function openPage() {
  if (url && product.value) {
    window.open(`${url}${product.value.pathname}`, '_blank');
  }
}

// Copies the URL source to the clipboard
async function copySource() {
  const source = urlSourcesStore.getSourceByPathname(inputPathname.value);
  if (source) {
    try {
      await navigator.clipboard.writeText(source);
      console.log('Source copied to clipboard');
    } catch (err) {
      console.error('Failed to copy source to clipboard:', err);
    }
  }
}
</script>